import React, { useState } from 'react';
import { Link as RouterLink, useLocation, useNavigate } from 'react-router-dom';
import { Alert, Box, Button, Link, Paper, TextField, Typography } from '@mui/material';
import { authApi, RESET_EMAIL_KEY } from '../../api/auth';
import { APP_NAME } from '../../constants';

type LocationState = { email?: string };

const VerifyOtpPage: React.FC = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const prefilledEmail =
    (location.state as LocationState | null)?.email || sessionStorage.getItem(RESET_EMAIL_KEY) || '';

  const [email, setEmail] = useState(prefilledEmail);
  const [otp, setOtp] = useState('');
  const [errors, setErrors] = useState<{ email?: string; otp?: string }>({});
  const [formError, setFormError] = useState('');
  const [loading, setLoading] = useState(false);

  const validate = () => {
    const next: typeof errors = {};
    if (!email.trim()) next.email = 'Email is required';
    else if (!/\S+@\S+\.\S+/.test(email)) next.email = 'Enter a valid email';
    if (!otp.trim()) next.otp = 'OTP is required';
    setErrors(next);
    return Object.keys(next).length === 0;
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setFormError('');
    if (!validate()) return;
    setLoading(true);
    try {
      const normalizedEmail = email.trim().toLowerCase();
      await authApi.verifyResetOtp(normalizedEmail, otp.trim());
      sessionStorage.setItem(RESET_EMAIL_KEY, normalizedEmail);
      navigate('/reset-password');
    } catch (err) {
      setFormError(err instanceof Error ? err.message : 'OTP verification failed');
    } finally {
      setLoading(false);
    }
  };

  return (
    <Box
      sx={{
        minHeight: '100vh',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        px: 2,
        backgroundColor: 'background.default',
      }}
    >
      <Paper variant="outlined" sx={{ width: '100%', maxWidth: 420, p: 4, borderRadius: 3 }}>
        <Typography variant="h1" sx={{ mb: 1 }}>
          Verify OTP
        </Typography>
        <Typography variant="body2" color="text.secondary" sx={{ mb: 3 }}>
          Enter the OTP sent to your registered email to continue.
        </Typography>

        {formError && (
          <Alert severity="error" sx={{ mb: 2 }}>
            {formError}
          </Alert>
        )}

        <Box component="form" onSubmit={handleSubmit}>
          <TextField
            fullWidth
            label="Email Address"
            type="email"
            value={email}
            onChange={(e) => setEmail(e.target.value)}
            error={Boolean(errors.email)}
            helperText={errors.email}
            margin="normal"
            required
          />
          <TextField
            fullWidth
            label="OTP"
            value={otp}
            onChange={(e) => setOtp(e.target.value)}
            error={Boolean(errors.otp)}
            helperText={errors.otp}
            margin="normal"
            required
            inputProps={{ inputMode: 'numeric', autoComplete: 'one-time-code' }}
          />
          <Button
            fullWidth
            type="submit"
            variant="contained"
            color="secondary"
            disabled={loading}
            sx={{ mt: 2 }}
          >
            {loading ? 'Verifying…' : 'Verify OTP'}
          </Button>
        </Box>

        <Box sx={{ mt: 2, display: 'flex', justifyContent: 'space-between' }}>
          <Link component={RouterLink} to="/forgot-password" variant="body2">
            Back
          </Link>
          <Link component={RouterLink} to="/login" variant="body2">
            Back to Login
          </Link>
        </Box>
        <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mt: 3 }}>
          {APP_NAME} Admin
        </Typography>
      </Paper>
    </Box>
  );
};

export default VerifyOtpPage;
